// ─────────────────────────────────────────────
//  src/components/portfolio/ExcelImporter.js
//  Smart Excel/CSV importer for portfolio holdings
//
//  Supports:
//  • .xlsx / .xls / .csv files
//  • Smart column auto-detection (any broker)
//  • Zerodha Console, Groww, AngelOne, Upstox presets
//  • ISIN → Symbol resolution
//  • Multiple lots → weighted average preview
//  • Preview table before confirming import
// ─────────────────────────────────────────────
import React, { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";

// ── ISIN → NSE Symbol map (top 200 stocks) ───
const ISIN_MAP = {
  "INE002A01018": "RELIANCE",   "INE467B01029": "TCS",
  "INE040A01034": "HDFCBANK",   "INE009A01021": "INFY",
  "INE090A01021": "ICICIBANK",  "INE030A01027": "HINDUNILVR",
  "INE062A01020": "SBIN",       "INE397D01024": "BHARTIARTL",
  "INE154A01025": "ITC",        "INE237A01028": "KOTAKBANK",
  "INE018A01030": "LT",         "INE238A01034": "AXISBANK",
  "INE296A01024": "BAJFINANCE", "INE021A01026": "ASIANPAINT",
  "INE585B01010": "MARUTI",     "INE860A01027": "HCLTECH",
  "INE044A01036": "SUNPHARMA",  "INE280A01028": "TITAN",
  "INE481G01011": "ULTRACEMCO", "INE075A01022": "WIPRO",
  "INE213A01029": "ONGC",       "INE752E01010": "POWERGRID",
  "INE733E01010": "NTPC",       "INE155A01022": "TATAMOTORS",
  "INE081A01020": "TATASTEEL",  "INE742F01042": "ADANIPORTS",
  "INE522F01014": "COALINDIA",  "INE918I01026": "BAJAJFINSV",
  "INE669C01036": "TECHM",      "INE239A01024": "NESTLEIND",
  "INE361B01024": "DIVISLAB",   "INE089A01023": "DRREDDY",
  "INE059A01026": "CIPLA",      "INE095A01012": "INDUSINDBK",
  "INE437A01024": "APOLLOHOSP", "INE019A01038": "JSWSTEEL",
  "INE029A01011": "BPCL",       "INE066A01021": "EICHERMOT",
  "INE158A01026": "HEROMOTOCO", "INE216A01030": "BRITANNIA",
  "INE047A01021": "GRASIM",     "INE423A01024": "ADANIENT",
  "INE192R01011": "TATACONSUM", "INE038A01020": "HINDALCO",
  "INE123W01016": "SBILIFE",    "INE795G01014": "HDFCLIFE",
  "INE628A01036": "UPL",        "INE205A01025": "VEDL",
  "INE101A01026": "M&M",        "INE917I01010": "BAJAJ-AUTO",
  "INE758T01015": "ZOMATO",     "INE542W01017": "NYKAA",
  "INE982J01020": "PAYTM",      "INE192A01025": "DMART",
  "INE318A01026": "PIDILITIND", "INE176B01034": "HAVELLS",
  "INE463A01038": "BERGEPAINT", "INE414G01012": "MUTHOOTFIN",
  "INE481I01014": "CHOLAFIN",   "INE765G01017": "ANGELONE",
  "INE848E01016": "IRFC",       "INE121J01017": "IRCTC",
  "INE669C01036": "TECHM",      "INE220G01021": "TATAPOWER",
  "INE467B01029": "TCS",        "INE070A01015": "RINFRA",
  "INE200M01013": "ANANTRAJ",
  // Groww export stocks
  "INE457A01014": "MAHABANK",   "INE040H01021": "SUZLON",
  "INF109KC1NT3": "ICICIGOLD",  "INE242A01010": "IOC",
  "INE750C01026": "MARKSANS",   "INE095N01031": "NBCC",
  // ETFs (INF prefix)
  "INF204KB13I2": "NIFTYBEES",  "INF740KA1897": "JUNIORBEES",
  "INF278P01309": "GOLDBEES",   "INF204KA1JN1": "LIQUIDBEES",
  "INF109KC1NT3": "ICICIGOLD",  "INF179K01WT6": "HDFCGOLD",
  // PSU Banks
  "INE028A01039": "BANKBARODA", "INE084A01016": "BANKINDIA",
  "INE112A01023": "CANBK",      "INE160A01022": "PNB",
  "INE565A01014": "UNIONBANK",  "INE134E01011": "RECLTD",
  "INE020B01018": "PFC",        "INE053F01010": "IREDA",
  "INE115A01026": "LICI",
  // IT
  "INE683C01011": "MPHASIS",    "INE124G01033": "LTIM",
  "INE150A01041": "PERSISTENT", "INE306R01017": "COFORGE",
  "INE591G01017": "KPITTECH",
  // Pharma
  "INE087B01035": "LUPIN",      "INE129A01019": "AUROPHARMA",
  "INE073K01018": "LAURUSLABS", "INE436A01026": "ALKEM",
  // Auto
  "INE775A01035": "TVSMOTOR",   "INE364A01020": "ASHOKLEY",
  "INE489L01023": "MOTHERSON",
  // FMCG
  "INE200A01026": "DABUR",      "INE274A01014": "GODREJCP",
  "INE259A01022": "MARICO",     "INE196A01026": "COLPAL",
  // Finance
  "INE148I01020": "LICIHSGFIN", "INE774D01024": "MANAPPURAM",
  // Metals
  "INE017A01032": "SAIL",
  // Realty
  "INE741K01010": "DLF",        "INE323A01026": "GODREJPROP",
};

// ── Column name synonyms for smart detection ──
const COL_ALIASES = {
  symbol: [
    "symbol","stock","scrip","ticker","instrument","scripname","stock name",
    "tradingsymbol","security","nse symbol","bse symbol","stock symbol",
    "share","equity","company","name","security name","instrument name",
  ],
  isin: ["isin","isin code","isin no","isin number"],
  qty: [
    "qty","quantity","shares","units","volume","holding qty","quantity held",
    "net qty","total qty","balance qty","no of shares","no. of shares",
    "shares held","net quantity",
  ],
  buy_price: [
    "buy price","avg price","average price","avg buy price","purchase price",
    "cost price","rate","ltp at purchase","avg cost","average cost",
    "avg buy rate","cost per share","avg rate","buying price","acquisition price",
    "average buy price","cost","avg. price",
    // Groww exact column name:
    "average buy price",
  ],
  date: [
    "date","purchase date","buy date","trade date","transaction date",
    "investment date","acquisition date","holding since","date of purchase",
    "date of acquisition","buy date","order date",
  ],
  exchange: [
    "exchange","market","segment","exch","exchange name","place of trade",
  ],
  qty_remaining: [
    "net qty","balance qty","remaining qty","current qty",
  ],
};

// Broker-specific presets (known column structures)
const BROKER_PRESETS = {
  zerodha: {
    name: "Zerodha Console",
    detect: (headers) => headers.some(h => /tradingsymbol|zerodha/i.test(h)),
    map: { symbol: "tradingsymbol", qty: "qty", buy_price: "avg. price", exchange: "exchange" },
  },
  groww: {
    name: "Groww",
    // Groww exports: has "Stock Name", "ISIN", "Quantity", "Average buy price"
    // Header row is NOT row 1 — file has metadata rows above it
    detect: (headers) => headers.some(h =>
      /stock name|average buy price|unrealised|closing price/i.test(h)
    ),
    map: {
      symbol:    "Stock Name",       // full name — resolved via ISIN
      isin:      "ISIN",
      qty:       "Quantity",
      buy_price: "Average buy price",
    },
  },
  upstox: {
    name: "Upstox",
    detect: (headers) => headers.some(h => /upstox|ltp at purchase/i.test(h)),
    map: { symbol: "symbol", qty: "quantity", buy_price: "ltp at purchase", exchange: "exchange" },
  },
  angelone: {
    name: "AngelOne",
    detect: (headers) => headers.some(h => /angel|net qty|holding qty/i.test(h)),
    map: { symbol: "symbol", isin: "isin", qty: "net qty", buy_price: "buy price", exchange: "exchange" },
  },
  icicidirect: {
    name: "ICICI Direct",
    detect: (headers) => headers.some(h => /icici|scrip name/i.test(h)),
    map: { symbol: "scrip name", qty: "qty", buy_price: "avg cost", date: "purchase date" },
  },
};

// ── Normalize header string ───────────────────
const norm = (s) => String(s || "").toLowerCase().trim().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

// ── Auto-detect column mapping ────────────────
function detectColumns(headers) {
  const normHeaders = headers.map(norm);
  const mapping     = {};

  for (const [field, aliases] of Object.entries(COL_ALIASES)) {
    for (let i = 0; i < normHeaders.length; i++) {
      if (aliases.includes(normHeaders[i])) {
        mapping[field] = headers[i];
        break;
      }
    }
  }

  // Broker preset override
  for (const preset of Object.values(BROKER_PRESETS)) {
    if (preset.detect(normHeaders)) {
      Object.assign(mapping, preset.map);
      mapping._broker = preset.name;
      break;
    }
  }

  return mapping;
}

// ── Parse a single raw row → holding object ──
function parseRow(raw, mapping) {
  const get = (field) => {
    const col = mapping[field];
    if (!col) return undefined;
    // Try exact match first, then normalized match
    return raw[col] !== undefined
      ? raw[col]
      : raw[Object.keys(raw).find(k => norm(k) === norm(col))];
  };

  const isinVal  = String(get("isin") || "").trim().toUpperCase();
  const rawSym   = String(get("symbol") || "").trim().toUpperCase();

  // Resolution order:
  // 1. ISIN → symbol (most reliable — covers Groww "Stock Name" = full name)
  // 2. Symbol column directly (if it looks like a ticker not a full name)
  let symbol = "";
  if (isinVal && ISIN_MAP[isinVal]) {
    symbol = ISIN_MAP[isinVal];                        // ISIN resolved ✓
  } else if (rawSym && !rawSym.includes(" ") && rawSym.length <= 20) {
    symbol = rawSym;                                   // Looks like a ticker ✓
  } else if (isinVal && isinVal.startsWith("IN")) {
    // ISIN present but not in map — use ISIN as fallback key (user can fix later)
    symbol = isinVal.slice(2, 8);                      // crude fallback
  }

  // Strip .NS / .BO suffixes
  symbol = symbol.replace(/\.(NS|BO)$/i, "").trim();

  if (!symbol || symbol.length < 2) return null;

  const qtyRaw   = get("qty") ?? get("qty_remaining");
  const priceRaw = get("buy_price");
  const qty      = parseFloat(String(qtyRaw  || "0").replace(/[^0-9.]/g, ""));
  const price    = parseFloat(String(priceRaw || "0").replace(/[^0-9.]/g, ""));

  if (!qty || qty <= 0 || !price || price <= 0) return null;

  // Exchange detection
  let exchange = String(get("exchange") || "").toUpperCase().trim();
  if (!exchange || exchange === "UNDEFINED") {
    // Guess from symbol suffix or isin
    exchange = isinVal.startsWith("INE") ? "NSE" : "NSE";
  }
  if (exchange.includes("BSE") || exchange === "B") exchange = "BSE";
  else exchange = "NSE";

  // Date parsing
  let date = "";
  const dateRaw = get("date");
  if (dateRaw) {
    // Excel serial date
    if (typeof dateRaw === "number") {
      const d = XLSX.SSF.parse_date_code(dateRaw);
      date = `${d.y}-${String(d.m).padStart(2,"0")}-${String(d.d).padStart(2,"0")}`;
    } else {
      const parsed = new Date(String(dateRaw));
      if (!isNaN(parsed)) date = parsed.toISOString().slice(0, 10);
    }
  }

  return { symbol, exchange, qty, buy_price: price, date, isin: isinVal || "" };
}

// ── Main ExcelImporter component ──────────────
export default function ExcelImporter({ onImport, onClose }) {
  const [step, setStep]       = useState("upload");  // upload | preview | done
  const [rows, setRows]       = useState([]);
  const [errors, setErrors]   = useState([]);
  const [broker, setBroker]   = useState("");
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState("");
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // ── Parse uploaded file ─────────────────────
  const parseFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];

        // ── KEY FIX: Find the real header row ──────────────────
        // Groww (and many brokers) have metadata rows before the actual table.
        // We scan row by row to find the first row that looks like a header
        // (contains known column keywords like "quantity", "isin", "price", "symbol").
        const HEADER_KEYWORDS = /qty|quantity|isin|price|symbol|stock|scrip|shares|amount|buy|avg/i;
        const rawSheet = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

        let headerRowIdx = 0;
        for (let i = 0; i < Math.min(rawSheet.length, 20); i++) {
          const rowStr = rawSheet[i].join(" ");
          const nonEmptyCells = rawSheet[i].filter(c => c !== "").length;
          // A header row has multiple non-empty cells AND contains known keywords
          if (nonEmptyCells >= 3 && HEADER_KEYWORDS.test(rowStr)) {
            headerRowIdx = i;
            break;
          }
        }

        // Build JSON from the detected header row onwards
        const headers = rawSheet[headerRowIdx].map(h => String(h).trim());
        const rawRows = [];
        for (let i = headerRowIdx + 1; i < rawSheet.length; i++) {
          const row = rawSheet[i];
          // Skip empty rows
          if (row.every(c => c === "" || c === null)) continue;
          const obj = {};
          headers.forEach((h, j) => { if (h) obj[h] = row[j] ?? ""; });
          rawRows.push(obj);
        }
        // ── END FIX ────────────────────────────────────────────

        if (!rawRows.length) { setErrors(["Could not find data rows in this file."]); return; }

        const colMap = detectColumns(headers);
        setMapping(colMap);
        setBroker(colMap._broker || "");

        const parsed = [], errs = [];
        rawRows.forEach((raw, i) => {
          const row = parseRow(raw, colMap);
          if (row) parsed.push(row);
          else if (Object.values(raw).some(v => v !== "")) {
            errs.push(`Row ${headerRowIdx + i + 2}: Could not parse — symbol/qty/price missing or zero`);
          }
        });

        if (!parsed.length) {
          setErrors([
            "No valid rows found. Check that your file has Symbol/ISIN, Quantity, and Buy Price columns.",
            ...errs.slice(0, 4),
          ]);
          return;
        }

        setRows(parsed);
        setErrors(errs.slice(0, 5));
        setStep("preview");
      } catch (err) {
        setErrors([`Parse error: ${err.message}`]);
      }
    };
    reader.readAsBinaryString(file);
  }, []);

  function handleFile(e) { parseFile(e.target.files[0]); }
  function handleDrop(e) {
    e.preventDefault(); setDragging(false);
    parseFile(e.dataTransfer.files[0]);
  }

  // ── Confirm import ──────────────────────────
  async function handleImport() {
    setImporting(true);
    try {
      await onImport(rows);
      setStep("done");
    } catch (err) {
      setErrors([`Import failed: ${err.message}`]);
    } finally {
      setImporting(false);
    }
  }

  // ── Download template ───────────────────────
  function downloadTemplate() {
    const ws  = XLSX.utils.aoa_to_sheet([
      ["Symbol","Exchange","Quantity","Buy Price","Date","Notes"],
      ["RELIANCE","NSE",10,2800,"2024-01-15","Initial buy"],
      ["TCS","NSE",5,3500,"2024-02-20","Added on dip"],
      ["HDFCBANK","NSE",20,1650,"2024-03-10",""],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Portfolio");
    XLSX.writeFile(wb, "StockIQ_Portfolio_Template.xlsx");
  }

  // ── Merge preview: group by symbol ──────────
  const merged = rows.reduce((acc, r) => {
    const key = r.symbol;
    if (!acc[key]) acc[key] = { symbol: r.symbol, exchange: r.exchange, qty: 0, totalCost: 0, lots: 0, date: r.date };
    acc[key].qty       += r.qty;
    acc[key].totalCost += r.qty * r.buy_price;
    acc[key].lots      += 1;
    return acc;
  }, {});
  const mergedRows = Object.values(merged).map(r => ({
    ...r, avg_price: r.totalCost / r.qty,
  }));

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>📥 Import Portfolio from Excel</div>
            {broker && <div style={styles.brokerBadge}>Detected: {broker}</div>}
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ── STEP: Upload ── */}
        {step === "upload" && (
          <div style={styles.body}>
            {/* Drop zone */}
            <div
              style={{ ...styles.dropZone, ...(dragging ? styles.dropZoneActive : {}) }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <div style={styles.dropIcon}>📊</div>
              <div style={styles.dropTitle}>Drop your Excel or CSV file here</div>
              <div style={styles.dropSub}>Supports .xlsx, .xls, .csv — from any broker</div>
              <input
                ref={fileRef} type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                onChange={handleFile}
              />
              <button style={styles.browseBtn}>Browse File</button>
            </div>

            {/* Broker support pills */}
            <div style={styles.brokerRow}>
              <span style={styles.brokerLabel}>Supported brokers:</span>
              {Object.values(BROKER_PRESETS).map(b => (
                <span key={b.name} style={styles.brokerPill}>{b.name}</span>
              ))}
              <span style={styles.brokerPill}>+ Any broker CSV</span>
            </div>

            {/* Template download */}
            <div style={styles.templateRow}>
              <span style={{ color: "#64748b", fontSize: "0.8rem" }}>
                Don't have a file? Use our template:
              </span>
              <button style={styles.templateBtn} onClick={downloadTemplate}>
                ⬇ Download Template
              </button>
            </div>

            {/* Column format guide */}
            <div style={styles.formatBox}>
              <div style={styles.formatTitle}>Required columns (any order, any name variant):</div>
              <div style={styles.formatGrid}>
                {[
                  { field: "Symbol / Stock / Scrip / ISIN", req: true },
                  { field: "Qty / Quantity / Shares", req: true },
                  { field: "Buy Price / Avg Price / Cost", req: true },
                  { field: "Date / Purchase Date", req: false },
                  { field: "Exchange (NSE/BSE)", req: false },
                ].map(f => (
                  <div key={f.field} style={styles.formatItem}>
                    <span style={{ color: f.req ? "#22c55e" : "#64748b" }}>{f.req ? "✓" : "○"}</span>
                    <span style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{f.field}</span>
                  </div>
                ))}
              </div>
            </div>

            {errors.length > 0 && (
              <div style={styles.errorBox}>
                {errors.map((e, i) => <div key={i} style={{ color: "#ef4444", fontSize: "0.8rem" }}>{e}</div>)}
              </div>
            )}
          </div>
        )}

        {/* ── STEP: Preview ── */}
        {step === "preview" && (
          <div style={styles.body}>
            <div style={styles.previewMeta}>
              <span style={{ color: "#22c55e", fontWeight: 600 }}>{mergedRows.length} stocks</span>
              <span style={{ color: "#64748b" }}> from </span>
              <span style={{ color: "#94a3b8" }}>{rows.length} rows</span>
              <span style={{ color: "#64748b" }}> in </span>
              <span style={{ color: "#60a5fa" }}>{fileName}</span>
              {broker && <span style={styles.brokerBadge}>{broker}</span>}
            </div>

            {/* Column mapping display */}
            <div style={styles.mappingRow}>
              {Object.entries(mapping).filter(([k]) => k !== "_broker").map(([field, col]) => (
                <div key={field} style={styles.mappingPill}>
                  <span style={{ color: "#64748b" }}>{field}</span>
                  <span style={{ color: "#475569" }}>→</span>
                  <span style={{ color: "#60a5fa", fontFamily: "var(--siq-mono)", fontSize: "0.72rem" }}>"{col}"</span>
                </div>
              ))}
            </div>

            {/* Preview table */}
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["Symbol","Exchange","Lots","Total Qty","Avg Buy Price","Est. Invested"].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mergedRows.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? {} : { background: "rgba(255,255,255,0.02)" }}>
                      <td style={{ ...styles.td, color: "#e2e8f0", fontWeight: 600, fontFamily: "var(--siq-mono)" }}>{r.symbol}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.exchBadge, color: r.exchange === "BSE" ? "#fbbf24" : "#22c55e" }}>
                          {r.exchange}
                        </span>
                      </td>
                      <td style={{ ...styles.td, color: "#64748b" }}>{r.lots}</td>
                      <td style={{ ...styles.td, fontFamily: "var(--siq-mono)" }}>{r.qty}</td>
                      <td style={{ ...styles.td, fontFamily: "var(--siq-mono)" }}>₹{r.avg_price.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td style={{ ...styles.td, fontFamily: "var(--siq-mono)", color: "#60a5fa" }}>
                        ₹{(r.qty * r.avg_price).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {errors.length > 0 && (
              <div style={{ ...styles.errorBox, marginTop: 8 }}>
                <div style={{ color: "#eab308", fontSize: "0.78rem", marginBottom: 4 }}>⚠ Skipped rows:</div>
                {errors.map((e, i) => <div key={i} style={{ color: "#64748b", fontSize: "0.75rem" }}>{e}</div>)}
              </div>
            )}
          </div>
        )}

        {/* ── STEP: Done ── */}
        {step === "done" && (
          <div style={{ ...styles.body, alignItems: "center", justifyContent: "center", textAlign: "center", gap: 16 }}>
            <div style={{ fontSize: "3rem" }}>✅</div>
            <div style={{ color: "#22c55e", fontWeight: 700, fontSize: "1.1rem" }}>
              {mergedRows.length} stocks imported successfully
            </div>
            <div style={{ color: "#64748b", fontSize: "0.85rem" }}>
              Your portfolio has been updated. Live prices will load shortly.
            </div>
            <button style={styles.importBtn} onClick={onClose}>View Portfolio →</button>
          </div>
        )}

        {/* Footer */}
        {step !== "done" && (
          <div style={styles.footer}>
            {step === "preview" && (
              <button style={styles.ghostBtn} onClick={() => setStep("upload")}>← Back</button>
            )}
            <div style={{ flex: 1 }} />
            <button style={styles.ghostBtn} onClick={onClose}>Cancel</button>
            {step === "preview" && (
              <button
                style={{ ...styles.importBtn, opacity: importing ? 0.7 : 1 }}
                onClick={handleImport}
                disabled={importing || !rows.length}
              >
                {importing ? "Importing…" : `Import ${mergedRows.length} Stocks`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline styles (matches SIQ dark theme) ────
const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 3000, padding: 20, backdropFilter: "blur(4px)",
  },
  modal: {
    background: "#0e1929", border: "1px solid #1e2d45",
    borderRadius: 16, width: "100%", maxWidth: 720,
    boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
    display: "flex", flexDirection: "column", maxHeight: "88vh",
    animation: "slideUp 0.2s ease",
  },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "18px 22px 14px", borderBottom: "1px solid #1e2d45", flexShrink: 0,
  },
  headerTitle: { fontSize: "1rem", fontWeight: 700, color: "#e2e8f0" },
  brokerBadge: {
    display: "inline-block", marginTop: 4, fontSize: "0.7rem", fontWeight: 600,
    color: "#60a5fa", background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.25)",
    borderRadius: 4, padding: "2px 8px",
  },
  closeBtn: {
    background: "none", border: "none", color: "#64748b",
    fontSize: "1.1rem", cursor: "pointer", padding: "2px 6px",
  },
  body: {
    flex: 1, overflowY: "auto", padding: "18px 22px",
    display: "flex", flexDirection: "column", gap: 14,
  },
  dropZone: {
    border: "1.5px dashed #1e2d45", borderRadius: 12,
    padding: "36px 24px", textAlign: "center", cursor: "pointer",
    transition: "all 0.15s", display: "flex", flexDirection: "column",
    alignItems: "center", gap: 8,
  },
  dropZoneActive: { borderColor: "#3b82f6", background: "rgba(59,130,246,0.05)" },
  dropIcon:  { fontSize: "2.5rem" },
  dropTitle: { fontSize: "0.95rem", fontWeight: 600, color: "#e2e8f0" },
  dropSub:   { fontSize: "0.78rem", color: "#64748b" },
  browseBtn: {
    marginTop: 8, padding: "8px 20px",
    background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)",
    borderRadius: 8, color: "#60a5fa", fontSize: "0.82rem", fontWeight: 600,
    cursor: "pointer", fontFamily: "var(--siq-font)",
  },
  brokerRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  brokerLabel: { fontSize: "0.72rem", color: "#475569", fontWeight: 600 },
  brokerPill: {
    fontSize: "0.68rem", color: "#64748b", background: "rgba(255,255,255,0.04)",
    border: "1px solid #1e2d45", borderRadius: 4, padding: "2px 8px",
  },
  templateRow: { display: "flex", alignItems: "center", gap: 10 },
  templateBtn: {
    padding: "5px 14px", background: "transparent",
    border: "1px solid #1e2d45", borderRadius: 6,
    color: "#94a3b8", fontSize: "0.78rem", cursor: "pointer",
    fontFamily: "var(--siq-font)", transition: "all 0.15s",
  },
  formatBox: {
    background: "rgba(255,255,255,0.02)", border: "1px solid #1e2d45",
    borderRadius: 8, padding: "12px 16px",
  },
  formatTitle: { fontSize: "0.72rem", fontWeight: 600, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" },
  formatGrid: { display: "flex", flexDirection: "column", gap: 4 },
  formatItem: { display: "flex", gap: 8, alignItems: "center" },
  errorBox: {
    background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)",
    borderRadius: 8, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 3,
  },
  previewMeta: { display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", flexWrap: "wrap" },
  mappingRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  mappingPill: {
    display: "flex", gap: 5, alignItems: "center", fontSize: "0.72rem",
    background: "rgba(255,255,255,0.03)", border: "1px solid #1e2d45",
    borderRadius: 4, padding: "2px 8px",
  },
  tableWrap: { overflowX: "auto", borderRadius: 8, border: "1px solid #1e2d45" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" },
  th: {
    padding: "9px 12px", textAlign: "left", fontWeight: 600,
    color: "#475569", fontSize: "0.7rem", textTransform: "uppercase",
    letterSpacing: "0.05em", background: "rgba(255,255,255,0.02)",
    borderBottom: "1px solid #1e2d45",
  },
  td: { padding: "8px 12px", color: "#94a3b8", verticalAlign: "middle" },
  exchBadge: {
    fontSize: "0.65rem", fontWeight: 700, padding: "1px 6px",
    borderRadius: 3, background: "rgba(255,255,255,0.05)",
  },
  footer: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 22px 16px", borderTop: "1px solid #1e2d45", flexShrink: 0,
  },
  ghostBtn: {
    padding: "8px 18px", background: "transparent", border: "1px solid #1e2d45",
    borderRadius: 8, color: "#64748b", fontSize: "0.82rem", cursor: "pointer",
    fontFamily: "var(--siq-font)",
  },
  importBtn: {
    padding: "9px 24px", background: "#22c55e", border: "none",
    borderRadius: 8, color: "#fff", fontSize: "0.84rem", fontWeight: 700,
    cursor: "pointer", fontFamily: "var(--siq-font)",
  },
};